import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import AdminMedia from "@/pages/AdminMedia";
import Layout from "@/components/Layout";
import PricingPage from "@/pages/PricingPage";
import MenuPage from "@/pages/MenuPage";
import FacilitiesPage from "@/pages/FacilitiesPage";
import EventsPage from "@/pages/EventsPage";
import VisitPage from "@/pages/VisitPage";
import TimesPage from "@/pages/TimesPage";
import HealthSafetyPage from "@/pages/HealthSafetyPage";
import GalleryPage from "@/pages/GalleryPage";
import ReviewsPage from "@/pages/ReviewsPage";
import ContactPage from "@/pages/ContactPage";
import DirectionsPage from "@/pages/DirectionsPage";
import EventDetailPage from "@/pages/EventDetailPage";
import BookingConfirmPage from "@/pages/BookingConfirmPage";
import AdminOrders from "@/pages/AdminOrders";
import AdminMenu from "@/pages/AdminMenu";
import AdminHoursPage from "@/pages/AdminHoursPage";
import AdminAnalytics from "@/pages/AdminAnalytics";
import CookieBanner from "@/components/CookieBanner";
import AiChatWidget from "@/components/AiChatWidget";
import MusicPlayer from "@/components/MusicPlayer";
import { useLocation } from "react-router-dom";

// fires one page-view to the park's stats on every route change (skips the staff portal)
function PageTracker() {
  const { pathname } = useLocation();
  const sentRef = React.useRef(false);
  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    const referrer = sentRef.current ? "" : document.referrer || "";
    sentRef.current = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({ path: pathname, referrer }),
    }).catch(() => {});
  }, [pathname]);
  return null;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[#fff7e8] p-6 text-center">
          <div>
            <img src="/logo.png" alt="Cool Runnings" className="mx-auto mb-6 w-40" />
            <p className="text-[#241c0d] font-bold">Something went off the track.</p>
            <button
              className="btn-gold mt-4"
              onClick={() => window.location.reload()}
              data-testid="error-reload-btn"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <PageTracker />
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/facilities" element={<FacilitiesPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:id" element={<EventDetailPage />} />
            <Route path="/times" element={<TimesPage />} />
            <Route path="/visit" element={<VisitPage />} />
            <Route path="/safety" element={<HealthSafetyPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/directions" element={<DirectionsPage />} />
            <Route path="/booking-confirm" element={<BookingConfirmPage />} />
            <Route path="/admin" element={<AdminHoursPage />} />
            <Route path="/admin/hours" element={<AdminHoursPage />} />
            <Route path="/admin/media" element={<AdminMedia />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/menu" element={<AdminMenu />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="*" element={<Home />} />
          </Route>
        </Routes>
        <CookieBanner />
        <AiChatWidget />
        <MusicPlayer />
      </BrowserRouter>
      <Toaster position="bottom-center" richColors closeButton />
    </ErrorBoundary>
  );
}

export default App;
